import { onMounted, onUnmounted, ref, Ref } from "vue";
import { getSocket } from "@/utils/socket";
import type { Socket } from "socket.io-client";
import type {
  BucketAutoCreatedPayload,
  BucketPromptManualPayload,
  LinkedInConnectionAcceptedPayload,
} from "@/dtos/BucketWebhook.dto";

/**
 * Options for the useBucketWebhooks composable
 */
export interface UseBucketWebhooksOptions {
  /**
   * Callback for handling bucket auto-created events
   * @param payload - The bucket auto-created payload
   */
  onBucketAutoCreated?: (payload: BucketAutoCreatedPayload) => void;

  /**
   * Callback for handling bucket manual prompt events
   * @param payload - The bucket prompt manual payload
   */
  onBucketPromptManual?: (payload: BucketPromptManualPayload) => void;

  /**
   * Callback for handling LinkedIn connection accepted events
   * @param payload - The LinkedIn connection accepted payload
   */
  onLinkedInConnectionAccepted?: (payload: LinkedInConnectionAcceptedPayload["payload"]) => void;

  /**
   * Maximum number of processed event IDs to keep in memory (default: 1000)
   */
  maxEventIds?: number;
}

/**
 * Composable for handling bucket webhook events via Socket.IO
 * Provides real-time bucket event handling with deduplication
 *
 * @param options - Configuration options for the composable
 * @returns Object containing connection state and control functions
 */
export function useBucketWebhooks(options: UseBucketWebhooksOptions = {}) {
  const {
    onBucketAutoCreated,
    onBucketPromptManual,
    onLinkedInConnectionAccepted,
    maxEventIds = 1000,
  } = options;

  const socket: Ref<Socket | null> = ref(null);
  const isConnected: Ref<boolean> = ref(false);
  const processedEventIds: Ref<Set<string>> = ref(new Set<string>());

  /**
   * Check if event has already been processed
   */
  const isEventProcessed = (eventId: string | null): boolean => {
    if (!eventId) return false;
    return processedEventIds.value.has(eventId);
  };

  /**
   * Mark event as processed
   */
  const markEventProcessed = (eventId: string | null): void => {
    if (!eventId) return;
    processedEventIds.value.add(eventId);

    // Cleanup old event IDs if we exceed the limit
    if (processedEventIds.value.size > maxEventIds) {
      const firstId = processedEventIds.value.values().next().value;
      if (firstId) {
        processedEventIds.value.delete(firstId);
      }
    }
  };

  /**
   * Handle bucket auto-created event
   */
  const handleBucketAutoCreated = (data: BucketAutoCreatedPayload) => {
    try {
      const eventId = `${data.bucketId}-${data.ts}`;
      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate bucket auto-created event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      if (onBucketAutoCreated) {
        onBucketAutoCreated(data);
      }
    } catch (error) {
      console.error("Error handling bucket auto-created event:", error);
    }
  };

  /**
   * Handle bucket manual prompt event
   */
  const handleBucketPromptManual = (data: BucketPromptManualPayload) => {
    try {
      const eventId = `${data.callLogId}-${data.ts}`;
      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate bucket prompt manual event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      if (onBucketPromptManual) {
        onBucketPromptManual(data);
      }
    } catch (error) {
      console.error("Error handling bucket prompt manual event:", error);
    }
  };

  /**
   * Handle LinkedIn connection accepted event
   */
  const handleLinkedInConnectionAccepted = (data: LinkedInConnectionAcceptedPayload) => {
    try {
      const { payload } = data;
      if (!payload) return;

      const eventId = payload.eventId;
      if (!eventId) return;

      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate LinkedIn connection accepted event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      if (onLinkedInConnectionAccepted) {
        onLinkedInConnectionAccepted(payload);
      }
    } catch (error) {
      console.error("Error handling LinkedIn connection accepted event:", error);
    }
  };

  /**
   * Setup socket connection and event listeners
   */
  const setupSocket = () => {
    try {
      socket.value = getSocket();
      isConnected.value = socket.value.connected;

      // Connection handlers
      socket.value.on("connect", () => {
        isConnected.value = true;
      });

      socket.value.on("disconnect", () => {
        isConnected.value = false;
      });

      socket.value.on("connect_error", (error) => {
        isConnected.value = false;
        console.error("❌ Bucket webhooks: Socket.IO connection error:", error);
      });

      // Bucket event handlers
      socket.value.on("bucket:auto-created", handleBucketAutoCreated);
      socket.value.on("bucket:prompt-manual", handleBucketPromptManual);
      socket.value.on("unipile:linkedin:connection_accepted", handleLinkedInConnectionAccepted);
    } catch (error) {
      console.error("Failed to setup bucket webhooks socket:", error);
      isConnected.value = false;
    }
  };

  /**
   * Cleanup socket connection and event listeners
   */
  const cleanupSocket = () => {
    if (socket.value) {
      try {
        socket.value.off("bucket:auto-created");
        socket.value.off("bucket:prompt-manual");
        socket.value.off("unipile:linkedin:connection_accepted");
        socket.value.off("connect");
        socket.value.off("disconnect");
        socket.value.off("connect_error");
      } catch (error) {
        console.error("Error cleaning up bucket webhooks socket:", error);
      }
    }
  };

  // Auto-setup socket in Composition API
  try {
    onMounted(() => {
      setupSocket();
    });

    onUnmounted(() => {
      cleanupSocket();
    });
  } catch (e) {
    console.error("Error:", e);
  }

  return {
    socket,
    isConnected,
    processedEventIds,
    setupSocket,
    cleanupSocket,
  };
}
